import React, {useState, useEffect} from 'react';
import { PackageTrackingDetails, RequestChangeResponse, RequestChangePostResponse } from '../../../types';
import { useTranslation } from 'react-i18next';
import SelectorComponent from '../Shared/DropdownSelector/SelectorComponent';
import FloatInputContainer from '../Shared/TextInput/FloatInputContainer';
import ActionIcon from '../Shared/Icons/ActionIcon';
import { InputRow, InputLabel, SelectorWrapper, TextInputWrapper, SubmitButton, OptionContainer, SelectedOptionContainer, SpinnerContainer } from './RequestChangeInputs';
import toast from '../../../Scripts/App/displayToastMessage';

interface SafePlaceData extends RequestChangeResponse {
    SafePlaces?: SafePlace[];
    SafePlaceDetails?: string;
};

interface SafePlace {
    Value?: number;
    Text: string;
};

interface DeliverToSafePlaceProps {
    packageDetails: PackageTrackingDetails;
};

const DeliverToSafePlaceComponent = ({packageDetails}: DeliverToSafePlaceProps) => {
    const [t] = useTranslation();

    const [safePlaceData, setSafePlaceData] = useState({ IsLoaded: false, Data: {} as SafePlaceData});
    const [selectedSafePlace, setSelectedSafePlace] = useState({} as SafePlace);
    const [safePlaceDetails, setSafePlaceDetails] = useState('');
    const [validationErrors, setValidationErrors] = useState<{[key: string]: string[]} | undefined>();

    const loadSafePlaceData = () => {
        const xhr = new XMLHttpRequest();
        const url = `Package/DeliverToSafePlaceData` + 
                    `?jobId=${packageDetails.JobId}` + 
                    `&trackingNumber=${packageDetails.TrackingNumber}`;
        xhr.open('GET', url, true);
        xhr.onload = () => {
            const data = JSON.parse(xhr.responseText) as SafePlaceData;
            setSafePlaceData({IsLoaded: true, Data: data});
            if(data.Success)
            {
                setSelectedSafePlace(data.SafePlaces && data.SafePlaces.length > 0 ? 
                    data.SafePlaces[0] : 
                    {Text: t('request_change_no_options_available'), Value: undefined}
                );
                setSafePlaceDetails(data.SafePlaceDetails ?? '');
            }
            if(data.Message)
            {
                toast(data.Message, data.MessageType ?? 'success');
            }
        };
        xhr.send();
        return xhr;
    };

    const reloadSafePlaceData = () => {
        setSafePlaceData({IsLoaded: false, Data: {} as SafePlaceData});
        loadSafePlaceData();
    };

    const sendSafePlaceRequest = () => {
        if(selectedSafePlace.Value == null) {
            toast(t('deliver_to_safe_place_header'), 'error');
            return;
        }

        const xhr = new XMLHttpRequest();
        const url = 'Package/DeliverToSafePlaceData';
        xhr.open('POST', url);
        xhr.setRequestHeader('Content-type', 'application/json');
        const data = JSON.stringify({
            TrackingNumber: packageDetails.TrackingNumber,
            JobId: packageDetails.JobId,
            IsDelivery: packageDetails.DeliveryOptions.IsDelivery,
            SafePlaceId: selectedSafePlace.Value,
            SafePlaceDetails: safePlaceDetails
        });
        
        xhr.onload = () => {
            const response = JSON.parse(xhr.responseText) as RequestChangePostResponse;

            if(response.Success) {
                // refresh the page
                const successMessage = packageDetails.DeliveryOptions.IsDelivery ? t('job_updated_delivery_safe_place') : t('job_updated_pickup_from_safe_place');
                window.location.href = `Package/Details?trackingNumber=${packageDetails.TrackingNumber}&message=${successMessage}`;
            }
            else 
            {
                setValidationErrors(response.ValidationErrors);
                if(response.Message)
                {
                    toast(response.Message, response.MessageType ?? 'success');
                }
            }
        }
        
        xhr.send(data);
    };

    useEffect(() => {
        const xhr = loadSafePlaceData();
        return () => { xhr.abort(); }
    }, []);
    
    const renderSelectedSafePlace = (safePlace) => 
        <SelectedOptionContainer>
            {safePlace.Text}
        </SelectedOptionContainer>

    const renderSafePlace = (safePlace) => 
        <OptionContainer key={safePlace.Value} onClick={() => setSelectedSafePlace(safePlace)}>
            {safePlace.Text}
        </OptionContainer>

    if(!safePlaceData.IsLoaded) //Loading
        return <SpinnerContainer />;
    
    else if(!safePlaceData.Data.Success) //Load error
        return(
            <InputRow>
                <div>{t('request_change_load_error_message')}</div>
                <ActionIcon title={'refresh'} onClick={reloadSafePlaceData}>refresh</ActionIcon>
            </InputRow>
        );

    return(
        <>
            <InputRow>
                <InputLabel>{t('safe_place')}</InputLabel>
                <SelectorWrapper>
                    <SelectorComponent
                        selectedItem={selectedSafePlace}
                        items={safePlaceData.Data.SafePlaces}
                        renderSelectedItem={renderSelectedSafePlace}
                        renderItems={(safePlaces) => safePlaces.map(renderSafePlace)}
                        fitToSize={false}
                    />
                </SelectorWrapper>
            </InputRow>
            <InputRow>
                <InputLabel>{t('safe_place_detail')}</InputLabel>
                <TextInputWrapper>
                    <FloatInputContainer
                        value={safePlaceDetails}
                        changeAction={(e) => setSafePlaceDetails(e.currentTarget.value)}
                        showErrors={true}
                        isValid={!(validationErrors && validationErrors!["SafePlaceDetails"]?.length > 0)}
                        invalidMessages={validationErrors && validationErrors!["SafePlaceDetails"]}
                    />
                </TextInputWrapper>
            </InputRow>
            <SubmitButton handleClick={sendSafePlaceRequest} />
        </>
    );
};

export default DeliverToSafePlaceComponent;